import { EyeIcon, PencilIcon, CubeIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { QUOTE_STATUS_BAR_COLORS } from '../constants/quoteStatusColors';

const PACKING_GUIDE_STATUSES = ['ONGOING', 'INSPECTION', 'READY'];

// Shared with SlicerQuotes via the constants module so the two palettes can't
// drift the next time a status is added to the state machine.
const STATUS_BAR_COLORS = QUOTE_STATUS_BAR_COLORS;

const formatStatusLabel = (status) =>
  status
    .split('_')
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');

const formatDateTime = (dateString) =>
  new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const formatShortDate = (dateString) =>
  dateString
    ? new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })
    : null;

const formatPriceTotal = (snapshot) => {
  if (!snapshot) return null;
  const cur = snapshot.currency || 'EUR';
  return `${(snapshot.totalPriceInCents / 100).toFixed(2)} ${cur}`;
};

function CompactCard({ quote, onDragStart, onViewDetails, onEditNotes, onOpenPacking, status, draggable }) {
  return (
    <div
      draggable={draggable}
      onDragStart={onDragStart}
      className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 cursor-move hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="font-semibold text-gray-900 mb-1">{quote.contact?.name}</div>
          <div className="text-sm text-gray-600 mb-1">{quote.contact?.email}</div>
          <div className="text-xs text-gray-500">{formatDateTime(quote.createdAt)}</div>
        </div>
        {quote.step != null && (
          <div className="flex flex-col items-end gap-1">
            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
              Step {quote.step}
            </span>
          </div>
        )}
      </div>

      <div className="space-y-2 mb-3 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-600">Quantity:</span>
          <span className="font-medium">{quote.quantity}</span>
        </div>
        {quote.priceSnapshot && (
          <div className="flex justify-between">
            <span className="text-gray-600">Total:</span>
            <span className="font-medium text-green-600 text-right">
              {(quote.priceSnapshot.totalPriceInCents / 100).toFixed(2)}{' '}
              {quote.priceSnapshot.currency || 'EUR'}
              {quote.quantity > 1 && (
                <>
                  <br />
                  <small>
                    ({(quote.priceSnapshot.unitPriceInCents / 100).toFixed(2)} / unit)
                  </small>
                </>
              )}
            </span>
          </div>
        )}
      </div>

      {quote.internalNotes && (
        <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
          <div className="font-medium text-yellow-800 mb-1">Internal Notes:</div>
          <div className="text-yellow-700 line-clamp-2">{quote.internalNotes}</div>
        </div>
      )}

      <div className="flex gap-2 pt-3 border-t border-gray-100">
        <button
          onClick={() => onViewDetails(quote._id)}
          className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm bg-blue-50 text-blue-700 rounded hover:bg-blue-100 transition-colors"
          title="View Details"
        >
          <EyeIcon className="h-4 w-4" />
          Details
        </button>
        <button
          onClick={() => onEditNotes(quote)}
          className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm bg-gray-50 text-gray-700 rounded hover:bg-gray-100 transition-colors"
          title="Edit Internal Notes"
        >
          <PencilIcon className="h-4 w-4" />
          Notes
        </button>
        {PACKING_GUIDE_STATUSES.includes(status) && (
          <button
            onClick={() => onOpenPacking(quote._id)}
            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm bg-indigo-50 text-indigo-700 rounded hover:bg-indigo-100 transition-colors"
            title="Packing Guide"
          >
            <CubeIcon className="h-4 w-4" />
            Pack
          </button>
        )}
      </div>
    </div>
  );
}

function DistributionBar({ perStatusCount, total }) {
  const entries = Object.entries(perStatusCount || {}).filter(([, n]) => n > 0);
  if (entries.length === 0 || !total) return null;
  return (
    <div className="flex w-full h-2 rounded-full overflow-hidden bg-gray-100" title="Status distribution">
      {entries.map(([status, n]) => (
        <div
          key={status}
          className={STATUS_BAR_COLORS[status] || STATUS_BAR_COLORS.DEFAULT}
          style={{ width: `${(n / total) * 100}%` }}
          title={`${formatStatusLabel(status)}: ${n}`}
        />
      ))}
    </div>
  );
}

function QuoteMiniRow({ quote, status, onViewDetails, onEditNotes, onOpenPacking }) {
  const total = formatPriceTotal(quote.priceSnapshot);
  const readyBy = formatShortDate(quote.calculatedReadyBy);
  return (
    <div className="flex items-start gap-2 py-2 border-b border-gray-100 last:border-b-0">
      {quote.thumbnailUrl && (
        <img
          src={quote.thumbnailUrl}
          alt=""
          className="w-10 h-10 rounded object-cover flex-shrink-0 bg-gray-100"
        />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium text-gray-900">×{quote.quantity}</span>
          {total && <span className="text-green-600 text-xs">{total}</span>}
          {readyBy && <span className="text-xs text-gray-500">· ready {readyBy}</span>}
        </div>
        {quote.internalNotes && (
          <div
            className="text-xs text-yellow-700 line-clamp-1 mt-0.5 flex items-center gap-1"
            title={quote.internalNotes}
          >
            <PencilSquareIcon className="w-3 h-3 flex-shrink-0" aria-hidden="true" />
            <span>{quote.internalNotes}</span>
          </div>
        )}
      </div>
      <div className="flex gap-1 flex-shrink-0">
        <button
          onClick={() => onViewDetails(quote._id)}
          className="p-1 text-blue-700 hover:bg-blue-50 rounded"
          title="View Details"
        >
          <EyeIcon className="h-4 w-4" />
        </button>
        <button
          onClick={() => onEditNotes(quote)}
          className="p-1 text-gray-700 hover:bg-gray-100 rounded"
          title="Edit Internal Notes"
        >
          <PencilIcon className="h-4 w-4" />
        </button>
        {PACKING_GUIDE_STATUSES.includes(status) && (
          <button
            onClick={() => onOpenPacking(quote._id)}
            className="p-1 text-indigo-700 hover:bg-indigo-50 rounded"
            title="Packing Guide"
          >
            <CubeIcon className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function GroupCard({
  group,
  status,
  subsetQuotes,
  compact,
  draggable = true,
  onDragStart,
  onViewDetails,
  onEditNotes,
  onOpenPacking,
}) {
  const handleDragStart = (e) => {
    onDragStart(e, group, status, subsetQuotes.map((q) => q._id));
  };

  if (compact) {
    return (
      <CompactCard
        quote={subsetQuotes[0]}
        status={status}
        draggable={draggable}
        onDragStart={handleDragStart}
        onViewDetails={onViewDetails}
        onEditNotes={onEditNotes}
        onOpenPacking={onOpenPacking}
      />
    );
  }

  const orderLabel = group.linkedOrderNumber || 'Standalone';
  const contactName = subsetQuotes[0]?.contact?.name || group.contact?.name;
  const readyBy = formatShortDate(group.calculatedReadyBy);

  return (
    <div
      draggable={draggable}
      onDragStart={handleDragStart}
      className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 cursor-move hover:shadow-md transition-shadow"
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <span
          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 truncate max-w-[60%]"
          title={orderLabel}
        >
          {orderLabel}
        </span>
        <span className="text-xs text-gray-500 whitespace-nowrap">
          {subsetQuotes.length}/{group.aggregate?.total ?? subsetQuotes.length} here
        </span>
      </div>

      <div className="text-sm font-medium text-gray-900 mb-1 truncate" title={contactName}>
        {contactName}
      </div>
      <div className="text-xs text-gray-500 mb-2">
        {readyBy && <span>Ready by {readyBy}</span>}
      </div>

      <div className="mb-3">
        <DistributionBar
          perStatusCount={group.aggregate?.perStatusCount}
          total={group.aggregate?.total}
        />
      </div>

      <div className="border-t border-gray-100">
        {subsetQuotes.map((q) => (
          <QuoteMiniRow
            key={q._id}
            quote={q}
            status={status}
            onViewDetails={onViewDetails}
            onEditNotes={onEditNotes}
            onOpenPacking={onOpenPacking}
          />
        ))}
      </div>
    </div>
  );
}
